import {
  createContext, useContext, useEffect, useRef, useState, useCallback,
} from 'react';
import { api, hasAuthToken } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { useToast } from './ToastProvider';
import {
  JOB_REGISTRY, jobCompletion, jobLabel, jobIcon, jobSubmitMessage,
} from './jobRegistry';

/**
 * BackgroundJobsProvider — ONE case-wide poller for background-job state.
 *
 * Replaces the per-modal `setInterval` loops: a single poll feeds the banner
 * AND fires a completion toast the moment any registry-known job goes terminal,
 * so a user who submitted then walked away (or closed the modal) still gets a
 * one-click "View redacted copy" / "Download" / "View checklist".
 *
 * Mounted high (NotepadHost, keyed by caseId) so it covers BOTH the case tabs
 * and the app-level deep-link viewer (where RedactionStudio renders), and so a
 * single poller survives tab switches. Seeds from `caseDashboard.background_jobs`
 * (Pool Governance P2) to avoid a duplicate fetch on first render.
 */

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

const BackgroundJobsContext = createContext(null);

export function useBackgroundJobs() {
  return useContext(BackgroundJobsContext) || {
    jobs: [],
    submitJob: async (_type, submitPromise) => submitPromise,
  };
}

export function BackgroundJobsProvider({ caseId, children }) {
  const { caseDashboard, dispatch } = useApp();
  const { showToast } = useToast();
  const [jobs, setJobs] = useState(() => caseDashboard?.background_jobs?.jobs || []);

  const timerRef = useRef(null);
  const hasJobsRef = useRef(false);
  hasJobsRef.current = jobs.length > 0;

  // Job ids we've observed in an ACTIVE (queued/processing) state. Only these
  // fire a completion toast when later seen terminal — so a page reload landing
  // on an already-finished job (still in the 60s terminal window) doesn't pop a
  // spurious toast for work the user never watched start.
  const seenActiveRef = useRef(new Set());
  const exportsSeenRef = useRef('');
  const toastedRef = useRef(new Set());
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  // poll() is wired by the effect; submitJob calls it for instant feedback.
  const pollRef = useRef(() => {});

  // A compilation being cut into documents (or re-cut after a correction made in
  // the inventory, the viewer or by Arc) creates AND removes child rows, which the
  // inventory's delta poll cannot express. When its `materialize_segments` job
  // leaves the active set, take the authoritative document list — here, because
  // this provider is mounted on every case tab — and tell that file's panel /
  // viewer / gallery to re-read its state.
  const activeCutJobsRef = useRef(new Map());          // job id → parent document id
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const followCutJobs = useCallback((list) => {
    const active = new Map();
    for (const j of list) {
      const st = (j.status || '').toLowerCase();
      if (j.job_type === 'materialize_segments' && !TERMINAL.has(st)) active.set(j.job_id, j.params?.document_id || null);
    }
    const finished = [...activeCutJobsRef.current].filter(([id]) => !active.has(id)).map(([, docId]) => docId);
    activeCutJobsRef.current = active;
    if (finished.length === 0 || !caseIdRef.current) return;
    api.get(`/documents/case/${caseIdRef.current}`)
      .then((data) => {
        dispatchRef.current?.({ type: 'SET_DOCUMENTS', docs: data.documents || data });
        for (const docId of finished) {
          if (!docId) continue;
          try { window.dispatchEvent(new CustomEvent('dc:segments-changed', { detail: { documentId: docId, source: 'job' } })); } catch { /* no window */ }
        }
      })
      .catch(() => { /* the inventory's own poll catches up */ });
  }, []);

  const fireCompletionToast = useCallback((j) => {
    const status = (j.status || '').toLowerCase();
    const type = j.job_type;
    const label = jobLabel(type);
    if (status === 'failed' || status === 'cancelled') {
      showToastRef.current({
        tone: 'error',
        icon: '⚠️',
        message: `${label} failed${j.error ? `: ${j.error}` : '.'}`,
      });
      return;
    }
    // The list endpoint returns the result envelope as `result` on terminal
    // jobs (and mirrors it into `progress`); fall back across both.
    const result = j.result || j.progress || {};
    const c = jobCompletion(type, result, { caseId: caseIdRef.current }) || {};
    const actions = [];
    if (c.cta && c.href) actions.push({ label: c.cta, href: c.href, primary: true });
    if (c.secondary?.cta && c.secondary?.href) {
      actions.push({ label: c.secondary.cta, href: c.secondary.href });
    } else if (c.secondary?.cta && c.secondary?.event) {
      const name = c.secondary.event;
      actions.push({ label: c.secondary.cta, onClick: () => window.dispatchEvent(new CustomEvent(name)) });
    }
    showToastRef.current({
      tone: 'success',
      icon: jobIcon(type) || '✅',
      message: c.message || `${label} — done.`,
      actions,
      autoDownload: c.autoDownload,
      downloadUrl: c.autoDownload ? c.href : null,
      ...(c.sticky ? { autoDismissMs: 0 } : {}),
    });
  }, []);

  // Merge a fresh poll payload into state + detect active→terminal transitions.
  const ingest = useCallback((incoming) => {
    const list = incoming || [];
    for (const j of list) {
      const status = (j.status || '').toLowerCase();
      const userInitiated = j.params?.initiated_by !== 'auto';
      if (!TERMINAL.has(status)) {
        seenActiveRef.current.add(j.job_id);
      } else if (
        userInitiated
        && seenActiveRef.current.has(j.job_id)
        && !toastedRef.current.has(j.job_id)
        && JOB_REGISTRY[j.job_type]?.completion
      ) {
        toastedRef.current.add(j.job_id);
        fireCompletionToast(j);
      }
    }
    followCutJobs(list);
    // An export that started or finished (seen running here or not): whoever
    // shows the Downloads count or list brings it up to date.
    const exportsNow = list.filter(j => j.job_type === 'export_documents').map(j => `${j.job_id}:${j.status}`).join(',');
    if (exportsNow !== exportsSeenRef.current) {
      exportsSeenRef.current = exportsNow;
      window.dispatchEvent(new CustomEvent('dc:exports-changed'));
    }
    setJobs(list);
  }, [fireCompletionToast, followCutJobs]);

  /**
   * submitJob(type, submitPromise) — POST a job (the caller owns the
   * endpoint-specific request), show the "we'll let you know" toast, optimistically
   * register the job id so its completion toast fires, and kick an immediate
   * poll so the banner shows progress without waiting a cadence tick. Returns
   * the raw submit response (`{ job_id, ... }`).
   */
  const submitJob = useCallback(async (type, submitPromise) => {
    const res = await submitPromise;
    if (res?.job_id) seenActiveRef.current.add(res.job_id);
    showToastRef.current({
      tone: 'info',
      icon: jobIcon(type),
      message: jobSubmitMessage(type),
    });
    pollRef.current();
    return res;
  }, []);

  useEffect(() => {
    if (!caseId) {
      setJobs([]);
      return undefined;
    }
    let active = true;
    const seeded = Boolean(caseDashboard?.background_jobs);

    // Adaptive cadence (ported from the old banner poller): poll fast (5s) only
    // while jobs are running, back off to 20s when idle, and skip while the tab
    // is hidden — the case page already runs several pollers.
    async function doPoll() {
      if (!active) return;
      if (document.visibilityState !== 'visible' || !hasAuthToken()) {
        // Hidden tab, or auth not restored yet (this provider mounts above
        // the route guard) — never fire an unauthenticated poll.
        schedule();
        return;
      }
      try {
        const data = await api.get(`/exploration/case/${caseId}/background-jobs`);
        if (active) ingest(data.jobs || []);
      } catch {
        // non-fatal
      }
      schedule();
    }

    function schedule() {
      if (!active) return;
      const next = hasJobsRef.current ? 5000 : 20000;
      timerRef.current = setTimeout(doPoll, next);
    }

    pollRef.current = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      doPoll();
    };

    // A boundary correction queues its re-cut job on the server (nothing calls
    // submitJob): poll now so the job is seen while it is still queued and the
    // fast cadence carries it to completion. 'job' = our own completion echo.
    const onSegmentsChanged = (e) => { if (e?.detail?.source !== 'job') pollRef.current(); };
    window.addEventListener('dc:segments-changed', onSegmentsChanged);

    if (seeded) {
      // Process the seed for transitions too, then defer the first network poll
      // by one cadence tick.
      ingest(caseDashboard.background_jobs.jobs || []);
      schedule();
    } else {
      doPoll();
    }

    return () => {
      active = false;
      window.removeEventListener('dc:segments-changed', onSegmentsChanged);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // caseDashboard intentionally not in deps — used only for the initial seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, ingest]);

  return (
    <BackgroundJobsContext.Provider value={{ jobs, submitJob }}>
      {children}
    </BackgroundJobsContext.Provider>
  );
}
